import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import DashboardNav from './DashboardNav';
import { MobileDashboardHeader, MobileNavDrawer } from './MobileDashboardChrome';
import { useMediaQuery } from '../hooks/useMediaQuery';
import {
  DASHBOARD_NAV_WIDTH_PX,
  layoutMobileMediaQuery,
  chapterDemoMainPadding,
  demoShellMobileMediaQuery,
} from '../designSystem';

const DEFAULT_FONT = "'Plus Jakarta Sans', -apple-system, sans-serif";

/**
 * Desktop: [DashboardNav | main]; Nova recommendations render fixed via `rightColumn` (not a grid track). Mobile: header + drawer + main; `rightColumn` is typically `NovaSidebarWithCollapse` (FAB + sheet).
 */
export default function DashboardAppShell({
  children,
  rightColumn = null,
  /** Third grid track when `rightColumn` is set (e.g. '220px' for fixed side panels). */
  rightColumnGridTrack = 'auto',
  fontFamily = DEFAULT_FONT,
  mainStyle = {},
  shellBackground = 'var(--sand)',
  /** Optional class on `<main>` (e.g. `DEMO_PRODUCT_INPUT_SCOPE_CLASS` for demo form styling). */
  mainClassName,
}) {
  const hasRecsColumn = Boolean(rightColumn);
  const m = useMediaQuery(hasRecsColumn ? demoShellMobileMediaQuery : layoutMobileMediaQuery);
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  const mainPad = hasRecsColumn && !m ? chapterDemoMainPadding(false) : hasRecsColumn && m ? chapterDemoMainPadding(true) : m ? 16 : 24;

  const mainEl = (
    <main
      className={mainClassName}
      style={{
        overflowY: 'auto',
        padding: mainPad,
        minWidth: 0,
        boxSizing: 'border-box',
        ...mainStyle,
      }}
    >
      {hasRecsColumn ? <div className="demo-shell-main-inner">{children}</div> : children}
    </main>
  );

  if (m) {
    return (
      <>
        <div
          className="demo-layout-grid"
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            background: shellBackground,
            fontFamily,
          }}
        >
          <MobileDashboardHeader onMenu={() => setNavOpen(true)} />
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{mainEl}</div>
        </div>
        {navOpen && <MobileNavDrawer onClose={() => setNavOpen(false)} />}
        {rightColumn}
      </>
    );
  }

  const cols = rightColumn ? `${DASHBOARD_NAV_WIDTH_PX}px minmax(0,1fr)` : `${DASHBOARD_NAV_WIDTH_PX}px minmax(0,1fr)`;

  return (
    <>
      <div
        className="demo-layout-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: cols,
          minHeight: '100vh',
          background: shellBackground,
          fontFamily,
        }}
      >
        <DashboardNav />
        {mainEl}
      </div>
      {rightColumn}
    </>
  );
}
